import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, ChevronDown, ChevronUp, Copy, Loader2, Upload, Users, Building2, Trash2, Mail, CheckCircle, LogIn, XCircle } from "lucide-react";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
      <div className="text-2xl font-extrabold text-[#0F172A]">{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

// ── Districts tab ────────────────────────────────────────────────────────────
function DistrictsTab({ adminKey }) {
  const [districts, setDistricts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", manager_name: "", manager_email: "" });
  const [agentForm, setAgentForm] = useState({ name: "", email: "", sigla: "" });
  const [agentDistrictId, setAgentDistrictId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);
  const [uploadAgent, setUploadAgent] = useState(null);
  const [uploadCount, setUploadCount] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/districts`, { params: { key: adminKey } });
      setDistricts(res.data.districts);
    } catch { setDistricts([]); }
    finally { setLoading(false); }
  };

  if (!districts && !loading) load();

  const createDistrict = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/admin/districts`, form, { params: { key: adminKey } });
      setLastCreated({ type: "district", ...res.data, ...form });
      setForm({ name: "", manager_name: "", manager_email: "" });
      setShowCreate(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || "Errore nella creazione del distretto"); }
  };

  const createAgent = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${API}/api/admin/districts/${agentDistrictId}/agents`,
        agentForm, { params: { key: adminKey } }
      );
      setLastCreated({ type: "agent", ...res.data });
      setAgentForm({ name: "", email: "", sigla: "" });
      setAgentDistrictId(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || "Errore nella creazione dell'agente"); }
  };

  const deleteAgent = async (districtId, agentId, agentName) => {
    if (!window.confirm(`Rimuovere l'agente "${agentName}" dal distretto? Il suo account tornerà al piano Free.`)) return;
    try {
      await axios.delete(`${API}/api/admin/districts/${districtId}/agents/${agentId}`, { params: { key: adminKey } });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Errore nella rimozione dell'agente"); }
  };

  const deleteDistrict = async (districtId, districtName) => {
    if (!window.confirm(`Eliminare il distretto "${districtName}"? Manager e agenti torneranno al piano Free.`)) return;
    try {
      await axios.delete(`${API}/api/admin/districts/${districtId}`, { params: { key: adminKey } });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Errore nell'eliminazione del distretto"); }
  };

  const handleExcelUpload = async (agentId, districtId, file) => {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws);
    const clients = rows.map(r => ({
      name: r["Nome"] || r["name"] || "",
      address: r["Indirizzo"] || r["address"] || "",
      city: r["Città"] || r["Citta"] || r["city"] || "",
    })).filter(c => c.name);
    try {
      const res = await axios.post(
        `${API}/api/admin/districts/${districtId}/upload-clients`,
        { clients }, { params: { key: adminKey, agent_id: agentId } }
      );
      setUploadCount(res.data.created);
      setUploadAgent(agentId);
    } catch (err) { alert(err.response?.data?.detail || "Errore nel caricamento"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-[#0F172A]">Distretti</h2>
        <button onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-1.5 bg-[#0F172A] text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-slate-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Nuovo distretto
        </button>
      </div>

      {/* Create district form */}
      {showCreate && (
        <form onSubmit={createDistrict} className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-3">
          <h3 className="font-semibold text-[#0F172A] text-sm">Crea distretto</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nome distretto</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="es. Nord Italia" className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nome area manager</label>
              <input required value={form.manager_name} onChange={e => setForm(f => ({ ...f, manager_name: e.target.value }))}
                placeholder="Mario Rossi" className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Email area manager</label>
              <input required type="email" value={form.manager_email} onChange={e => setForm(f => ({ ...f, manager_email: e.target.value }))}
                placeholder="manager@azienda.it" className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-[#0F172A] text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-slate-800 transition-colors">Crea</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-xs text-slate-500 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50">Annulla</button>
          </div>
        </form>
      )}

      {/* Last created credentials box */}
      {lastCreated && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
              {lastCreated.type === "district" ? "Distretto creato" : "Agente creato"} — credenziali di accesso
            </span>
            <button onClick={() => setLastCreated(null)} className="text-xs text-emerald-500 hover:text-emerald-700">Chiudi</button>
          </div>
          {lastCreated.type === "district" && lastCreated.manager_password && (
            <CredentialRow label="Area manager" email={lastCreated.manager_email} password={lastCreated.manager_password} />
          )}
          {lastCreated.type === "agent" && (
            <CredentialRow label={`Agente (${lastCreated.sigla})`} email={lastCreated.email} password={lastCreated.password} />
          )}
        </div>
      )}

      {/* Upload result */}
      {uploadCount !== null && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700 flex items-center justify-between">
          <span>{uploadCount} clienti importati per l'agente</span>
          <button onClick={() => { setUploadCount(null); setUploadAgent(null); }} className="text-blue-400 hover:text-blue-700 text-xs">Chiudi</button>
        </div>
      )}

      {/* Districts list */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : districts?.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">Nessun distretto creato</div>
      ) : (
        <div className="space-y-3">
          {districts?.map(d => (
            <div key={d.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="w-full flex items-center justify-between px-5 py-4">
                <button onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  className="flex-1 flex items-center gap-3 text-left hover:opacity-80 transition-opacity">
                  <div className="w-9 h-9 bg-[#EFF6FF] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  <div>
                    <div className="font-bold text-[#0F172A] text-sm">{d.name}</div>
                    <div className="text-xs text-slate-400">{d.manager_name} · {d.agent_count} agenti · {d.total_clients} clienti</div>
                  </div>
                </button>
                <div className="flex items-center gap-2 ml-2">
                  <button onClick={() => deleteDistrict(d.id, d.name)}
                    data-testid={`delete-district-${d.id}`}
                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedId === d.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>

              {expandedId === d.id && (
                <div className="border-t border-slate-100 px-5 py-4 space-y-4">
                  {/* Agents list */}
                  {d.agents?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Agenti</p>
                      {d.agents.map(a => (
                        <div key={a.user_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5">
                          <div>
                            <div className="font-medium text-[#0F172A] text-sm">{a.name}</div>
                            <div className="text-xs text-slate-400">{a.email} · {a.sigla}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {uploadAgent === a.user_id && uploadCount !== null && (
                              <span className="text-xs text-emerald-600 font-medium">{uploadCount} imp.</span>
                            )}
                            <label className="flex items-center gap-1.5 bg-white border border-slate-200 text-xs font-medium px-3 py-1.5 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                              <Upload className="w-3.5 h-3.5 text-[#2563EB]" /> Excel
                              <input type="file" accept=".xlsx,.xls" className="hidden"
                                onChange={e => e.target.files[0] && handleExcelUpload(a.user_id, d.id, e.target.files[0])} />
                            </label>
                            <button onClick={() => deleteAgent(d.id, a.user_id, a.name)}
                              data-testid={`delete-agent-${a.user_id}`}
                              className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add agent form / button */}
                  {agentDistrictId === d.id ? (
                    <form onSubmit={createAgent} className="bg-slate-50 rounded-xl p-3 space-y-2">
                      <p className="text-xs font-semibold text-slate-500">Nuovo agente</p>
                      <div className="grid grid-cols-3 gap-2">
                        <input required value={agentForm.name} onChange={e => setAgentForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="Nome" className="border border-slate-200 rounded-xl px-3 py-2 text-xs" />
                        <input required type="email" value={agentForm.email} onChange={e => setAgentForm(f => ({ ...f, email: e.target.value }))}
                          placeholder="Email" className="border border-slate-200 rounded-xl px-3 py-2 text-xs" />
                        <input required value={agentForm.sigla} onChange={e => setAgentForm(f => ({ ...f, sigla: e.target.value.toUpperCase() }))}
                          placeholder="Sigla (es. AGM)" maxLength={5} className="border border-slate-200 rounded-xl px-3 py-2 text-xs uppercase" />
                      </div>
                      <p className="text-[10px] text-slate-400">Password generata: sigla + anno (es. AGM2026)</p>
                      <div className="flex gap-2">
                        <button type="submit" className="bg-[#0F172A] text-white text-xs font-bold px-3 py-1.5 rounded-xl hover:bg-slate-800 transition-colors">Crea agente</button>
                        <button type="button" onClick={() => setAgentDistrictId(null)} className="text-xs text-slate-500 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50">Annulla</button>
                      </div>
                    </form>
                  ) : (
                    <button onClick={() => setAgentDistrictId(d.id)}
                      className="flex items-center gap-1.5 text-xs text-[#2563EB] font-semibold hover:underline">
                      <Plus className="w-3.5 h-3.5" /> Aggiungi agente
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CredentialRow({ label, email, password }) {
  const copy = (text) => { navigator.clipboard.writeText(text); };
  return (
    <div className="bg-white border border-emerald-200 rounded-xl p-3 space-y-1">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-700">Email: <span className="font-mono font-bold">{email}</span></span>
        <button onClick={() => copy(email)} className="text-slate-400 hover:text-slate-700"><Copy className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-700">Password: <span className="font-mono font-bold text-emerald-700">{password}</span></span>
        <button onClick={() => copy(password)} className="text-slate-400 hover:text-slate-700"><Copy className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [emailStatus, setEmailStatus] = useState({});
  const [uploadUserStatus, setUploadUserStatus] = useState({});

  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [fixingDuplicates, setFixingDuplicates] = useState(false);

  // Auto-login se si torna dall'impersonificazione
  useEffect(() => {
    const savedKey = localStorage.getItem("ve_admin_saved_key");
    if (savedKey && !data) {
      setKey(savedKey);
      setLoading(true);
      axios.get(`${API}/api/admin/stats`, { params: { key: savedKey } })
        .then(res => setData(res.data))
        .catch(() => localStorage.removeItem("ve_admin_saved_key"))
        .finally(() => setLoading(false));
    }
  }, []);

  async function login(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/stats`, { params: { key } });
      localStorage.setItem("ve_admin_saved_key", key);
      setData(res.data);
    } catch {
      setError("Password errata o errore di connessione.");
    } finally {
      setLoading(false);
    }
  }

  async function setSubscription(userId, sub) {
    try {
      await axios.post(`${API}/api/admin/set-subscription`, null, { params: { key, user_id: userId, subscription: sub } });
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.user_id === userId ? { ...u, subscription: sub } : u)
      }));
    } catch {
      alert("Errore nell'aggiornamento abbonamento");
    }
  }

  async function deleteUser(userId, userName) {
    if (!window.confirm(`Eliminare l'account di "${userName}"?\nVerranno cancellati anche tutti i suoi clienti e appuntamenti.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { params: { key } });
      setData(prev => ({ ...prev, users: prev.users.filter(u => u.user_id !== userId), total: prev.total - 1 }));
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  }

  async function toggleWelcomeSent(userId, currentValue) {
    setEmailStatus(prev => ({ ...prev, [userId]: true })); // loading
    try {
      const res = await axios.post(`${API}/api/admin/mark-welcome-sent`, null, { params: { key, user_id: userId } });
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.user_id === userId ? { ...u, welcome_email_sent: res.data.welcome_email_sent } : u)
      }));
    } catch {
      alert("Errore nell'aggiornamento");
    } finally {
      setEmailStatus(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  }

  async function handleExcelUploadUser(userId, file) {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws);
    const clients = rows.map(r => ({
      name: r["Nome"] || r["name"] || "",
      address: r["Indirizzo"] || r["address"] || "",
      city: r["Città"] || r["Citta"] || r["city"] || "",
      frequency_weeks: r["Frequenza"] || r["frequency_weeks"] || 2,
      preferred_day: r["GiornoPreferito"] !== undefined ? r["GiornoPreferito"] : (r["preferred_day"] !== undefined ? r["preferred_day"] : null),
    })).filter(c => c.name);
    try {
      const res = await axios.post(
        `${API}/api/admin/upload-clients-user`,
        { clients }, { params: { key, user_id: userId } }
      );
      setUploadUserStatus(prev => ({ ...prev, [userId]: res.data.created }));
      setTimeout(() => setUploadUserStatus(prev => { const n = { ...prev }; delete n[userId]; return n; }), 5000);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nel caricamento Excel");
    }
  }

  async function fixReschedules() {
    try {
      const res = await axios.post(`${API}/api/admin/fix-reschedules`, null, { params: { key } });
      const d = res.data;
      alert(`Corretti: ${d.rescheduled || 0} riprogrammati, ${d.corrected || 0} date corrette`);
    } catch (err) { alert(err.response?.data?.detail || "Errore"); }
  }

  async function checkDuplicates() {
    setDuplicateLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/duplicates`, { params: { key } });
      setDuplicateData(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nel controllo duplicati");
    } finally {
      setDuplicateLoading(false);
    }
  }

  async function fixAllDuplicates() {
    if (!window.confirm(`Rimuovere tutti i duplicati? Verrà mantenuta una copia per ogni appuntamento. Questa operazione è IRREVERSIBILE.`)) return;
    setFixingDuplicates(true);
    try {
      const res = await axios.post(`${API}/api/admin/fix-duplicates`, null, { params: { key } });
      const d = res.data;
      alert(`Rimossi ${d.removed} appuntamenti duplicati (${d.groups} gruppi corretti).`);
      setDuplicateData(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nella rimozione duplicati");
    } finally {
      setFixingDuplicates(false);
    }
  }

  async function cancelSubscription(userId, userName) {
    if (!window.confirm(`Cancellare l'abbonamento di "${userName}"?\n\n• Il piano verrà portato a Free nel database\n• Se ha pagato con Stripe, la subscription verrà cancellata anche lì\n\nL'operazione è irreversibile.`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/cancel-subscription`, null, { params: { key, user_id: userId } });
      const d = res.data;
      if (d.warning) {
        alert(`Abbonamento cancellato.\n\nNota: ${d.warning}`);
      } else {
        alert(`Abbonamento cancellato con successo (Stripe + database).`);
      }
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.user_id === userId ? { ...u, subscription: "free" } : u)
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nella cancellazione dell'abbonamento");
    }
  }

  async function impersonateUser(userId, userLabel) {
    if (!window.confirm(`Vuoi accedere come "${userLabel}"?\nPotrai tornare all'admin in qualsiasi momento.`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/impersonate/${userId}`, null, { params: { key } });
      localStorage.setItem("ve_admin_key", key);
      localStorage.setItem("ve_admin_saved_key", key);
      localStorage.setItem("ve_token", res.data.token);
      localStorage.setItem("ve_user_cache", JSON.stringify(res.data.user));
      window.location.href = "/app";
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nell'accesso");
    }
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-xl" />
            <div>
              <div className="text-white font-bold text-lg">venditoreElite</div>
              <div className="text-white/40 text-xs">Pannello Amministratore</div>
            </div>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-xs text-white/50 mb-1">Password admin</label>
              <input type="password" value={key} onChange={e => setKey(e.target.value)}
                placeholder="••••••••••••"
                className="w-full bg-white/10 text-white border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-white/30 placeholder-white/20"
                data-testid="admin-password-input" />
            </div>
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button type="submit" disabled={loading || !key}
              className="w-full bg-white text-[#0F172A] font-bold py-3 rounded-xl text-sm hover:bg-slate-100 transition disabled:opacity-40"
              data-testid="admin-login-btn">
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="w-9 h-9 rounded-xl" />
          <div>
            <div className="font-bold text-[#0F172A] text-base">Pannello Admin</div>
            <div className="text-slate-400 text-xs">venditoreElite</div>
          </div>
        </div>
        <button onClick={() => { setData(null); setKey(""); localStorage.removeItem("ve_admin_saved_key"); }}
          className="text-xs text-slate-400 hover:text-slate-700 transition">Esci</button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Utenti totali" value={data.total} />
        <StatCard label="Totale clienti" value={data.users.reduce((s, u) => s + u.n_clients, 0)} />
        <StatCard label="Appuntamenti" value={data.users.reduce((s, u) => s + u.n_appointments, 0)} />
      </div>

      {/* Azioni di manutenzione */}
      <div className="mb-5 flex gap-2 flex-wrap">
        <button onClick={fixReschedules}
          data-testid="fix-reschedules-btn"
          className="flex items-center gap-2 text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors px-4 py-2 rounded-xl">
          Correggi riprogrammazioni sbagliate
        </button>
        <button onClick={checkDuplicates} disabled={duplicateLoading}
          data-testid="check-duplicates-btn"
          className="flex items-center gap-2 text-xs font-semibold bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-colors px-4 py-2 rounded-xl disabled:opacity-40">
          {duplicateLoading ? "Analisi..." : "Controlla duplicati"}
        </button>
      </div>

      {/* Pannello risultati duplicati */}
      {duplicateData && (
        <div className="mb-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="duplicates-panel">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-[#0F172A] text-sm">Analisi duplicati</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {duplicateData.total_groups === 0
                  ? "Nessun duplicato trovato"
                  : `${duplicateData.total_groups} gruppi duplicati — ${duplicateData.total_extra_copies} copie extra da rimuovere`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {duplicateData.total_groups > 0 && (
                <button onClick={fixAllDuplicates} disabled={fixingDuplicates}
                  data-testid="fix-all-duplicates-btn"
                  className="text-xs font-bold bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-40">
                  {fixingDuplicates ? "Rimozione..." : `Rimuovi ${duplicateData.total_extra_copies} duplicati`}
                </button>
              )}
              <button onClick={() => setDuplicateData(null)} className="text-xs text-slate-400 hover:text-slate-700 px-3 py-2 rounded-xl hover:bg-slate-50">
                Chiudi
              </button>
            </div>
          </div>

          {duplicateData.total_groups === 0 ? (
            <div className="px-5 py-8 text-center">
              <div className="text-emerald-500 font-semibold text-sm">Ottimo! Nessun appuntamento duplicato trovato.</div>
              <div className="text-slate-400 text-xs mt-1">Il database è pulito.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {duplicateData.users_with_duplicates.map(u => (
                <div key={u.user_id} className="px-5 py-3">
                  <div className="font-medium text-[#0F172A] text-sm">{u.user_name || u.user_email}</div>
                  <div className="text-xs text-slate-400 mb-2">{u.user_email}</div>
                  <div className="space-y-1">
                    {u.duplicates.map((d, i) => (
                      <div key={i} className="flex items-center justify-between bg-red-50 rounded-xl px-3 py-2">
                        <span className="text-xs text-slate-700">
                          <span className="font-medium">{d.client_name}</span>
                          <span className="text-slate-400"> — {new Date(d.scheduled_date).toLocaleDateString("it-IT")}</span>
                        </span>
                        <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                          {d.count} copie (+{d.extra_copies} extra)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {[
          { id: "users", icon: Users, label: "Utenti" },
          { id: "districts", icon: Building2, label: "Distretti" },
        ].map(({ id, icon: Icon, label }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${activeTab === id ? "border-[#0F172A] text-[#0F172A]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "users" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-[#0F172A] text-sm">Utenti registrati</h2>
            <span className="text-xs text-slate-400">{data.total} totali</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="text-left px-5 py-3 font-medium">Utente</th>
                  <th className="text-left px-3 py-3 font-medium hidden sm:table-cell">Registrazione</th>
                  <th className="text-center px-3 py-3 font-medium">Clienti</th>
                  <th className="text-center px-3 py-3 font-medium">Appunt.</th>
                  <th className="text-center px-3 py-3 font-medium hidden sm:table-cell">Login</th>
                  <th className="text-center px-3 py-3 font-medium">Piano</th>
                  <th className="text-center px-3 py-3 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.users.map((u, i) => {
                  const isPremium = u.subscription === "premium";
                  const isBusiness = u.subscription === "business";
                  const rowClass = isPremium
                    ? "border-l-4 border-l-amber-500 bg-amber-100 hover:bg-amber-200 transition-colors"
                    : isBusiness
                    ? "border-l-4 border-l-blue-500 bg-blue-100 hover:bg-blue-200 transition-colors"
                    : "hover:bg-slate-50 transition-colors";
                  const emailLoading = emailStatus[u.user_id];
                  const welcomeSent = u.welcome_email_sent;
                  const uploadCount = uploadUserStatus[u.user_id];
                  return (
                  <tr key={u.user_id} className={rowClass} data-testid={`admin-user-row-${i}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-[#0F172A] truncate max-w-[180px]">{u.name || "—"}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[180px]">{u.email}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500 hidden sm:table-cell whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-3 text-center"><span className="font-semibold text-[#0F172A]">{u.n_clients}</span></td>
                    <td className="px-3 py-3 text-center"><span className="font-semibold text-[#0F172A]">{u.n_appointments}</span></td>
                    <td className="px-3 py-3 text-center hidden sm:table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_google ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                        {u.is_google ? "Google" : "Email"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <select value={u.subscription || "free"} onChange={e => setSubscription(u.user_id, e.target.value)}
                        data-testid={`sub-select-${i}`}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white cursor-pointer">
                        <option value="free">Free</option>
                        <option value="premium">Premium</option>
                        <option value="business">Business</option>
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        {/* Flag "email di benvenuto inviata" — solo segna, non invia */}
                        <button
                          onClick={() => toggleWelcomeSent(u.user_id, welcomeSent)}
                          disabled={emailLoading}
                          title={welcomeSent ? "Email segnata come inviata — clicca per rimuovere" : "Segna email di benvenuto come inviata"}
                          data-testid={`mark-welcome-btn-${i}`}
                          className={`p-1.5 rounded-lg transition-colors ${
                            emailLoading ? "opacity-40 animate-pulse text-slate-400"
                            : welcomeSent ? "text-emerald-500 bg-emerald-50 hover:bg-red-50 hover:text-red-400"
                            : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
                          }`}>
                          {welcomeSent ? <CheckCircle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                        </button>

                        {/* Upload Excel (solo Premium o Business) */}
                        {(isPremium || isBusiness) && (
                          <label
                            title="Carica Excel giro visite"
                            data-testid={`upload-excel-btn-${i}`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer relative">
                            {uploadCount !== undefined
                              ? <span className="text-[10px] font-bold text-emerald-600">{uploadCount} imp.</span>
                              : <Upload className="w-4 h-4" />}
                            <input type="file" accept=".xlsx,.xls" className="hidden"
                              onChange={e => e.target.files[0] && handleExcelUploadUser(u.user_id, e.target.files[0])} />
                          </label>
                        )}
                        {/* Accedi come questo utente */}
                        <button
                          onClick={() => impersonateUser(u.user_id, u.name || u.email)}
                          title="Accedi come questo utente"
                          data-testid={`impersonate-btn-${i}`}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <LogIn className="w-4 h-4" />
                        </button>
                        {/* Cancella abbonamento (solo Premium/Business) */}
                        {(isPremium || isBusiness) && (
                          <button
                            onClick={() => cancelSubscription(u.user_id, u.name || u.email)}
                            title="Cancella abbonamento (Stripe + DB)"
                            data-testid={`cancel-sub-btn-${i}`}
                            className="p-1.5 rounded-lg text-slate-300 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {/* Elimina account */}
                        <button
                          onClick={() => deleteUser(u.user_id, u.name || u.email)}
                          title="Elimina account"
                          data-testid={`delete-user-btn-${i}`}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "districts" && <DistrictsTab adminKey={key} />}
    </div>
  );
}
